import React, { useEffect, useState, useRef } from 'react';
import { Modal, Box, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel, Autocomplete, Grid, ListItemText, Chip, IconButton, FormControlLabel, Checkbox, Divider } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';
import AddResourceModal from './AddResourceModal';
import SuggestedEmployees from './SuggestedEmployees';

function ScheduleShiftModal(props) {
  const [scheduleName, setScheduleName] = useState('');
  const [resource, setResource] = useState('');
  const [resourceName, setResourceName] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date());
  const [maxCapacity, setMaxCapacity] = useState(1);
  const [notes, setNotes] = useState('');
  const [taskType, setTaskType] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [singleEmployeeOnly, setSingleEmployeeOnly] = useState(true);
  // Customers selection (optional at creation, but if provided sesasions will be auto-created)
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [preAuthList, setPreAuthList] = useState([]); // only used for day care remaining units display
  const [isAddResourceModalOpen, setIsAddResourceModalOpen] = useState(false);

  // ── Matchmaking state ──────────────────────────────────────────
  const [masterSkills, setMasterSkills] = useState([]);
  const [masterPreferences, setMasterPreferences] = useState([]);
  const [requiredSkills, setRequiredSkills] = useState([]);
  const [requiredPreferences, setRequiredPreferences] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const matchDebounceRef = useRef(null);

  const handleOpenAddResourceModal = () => setIsAddResourceModalOpen(true);
  const handleCloseAddResourceModal = () => setIsAddResourceModalOpen(false);

  // ── Fetch master skills / preferences on open ──────────────────
  useEffect(() => {
    if (props.open && props.comp_id) {
      axios.get(APIURL + '/caregiverPreferences/masterSkills', { params: { com_id: props.comp_id } })
        .then(res => setMasterSkills(res.data.map(s => s.skill_name)))
        .catch(() => setMasterSkills([]));
      axios.get(APIURL + '/caregiverPreferences/masterPreferences', { params: { com_id: props.comp_id } })
        .then(res => setMasterPreferences(res.data.map(p => p.preference_name)))
        .catch(() => setMasterPreferences([]));
    }
  }, [props.open, props.comp_id]);

  // ── Debounced matchmaking call ─────────────────────────────────
  const hasCriteria = requiredSkills.length > 0 || requiredPreferences.length > 0;

  useEffect(() => {
    if (!props.open || !props.comp_id || !hasCriteria) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    // Build start/end time strings
    const sTime = moment(startTime).isValid() ? moment(startTime).format('HH:mm') : null;
    const eTime = moment(endTime).isValid() ? moment(endTime).format('HH:mm') : null;
    const sDate = moment(selectedDate).isValid() ? moment(selectedDate).format('YYYY-MM-DD') : null;

    // Get location address from the selected resource (resources are JOINed with location table)
    const locZip = resource?.loc_zip || null;
    const locCity = resource?.loc_city || null;
    const locState = resource?.loc_state || null;

    setSuggestionsLoading(true);

    if (matchDebounceRef.current) clearTimeout(matchDebounceRef.current);
    matchDebounceRef.current = setTimeout(() => {
      axios.post(APIURL + '/caregiverPreferences/matchmaking', {
        com_id: props.comp_id,
        shift_date: sDate,
        start_time: sTime,
        end_time: eTime,
        required_skills: requiredSkills,
        required_preferences: requiredPreferences,
        location_zip: locZip,
        location_city: locCity,
        location_state: locState,
      })
        .then(res => setSuggestions(res.data || []))
        .catch((err) => {
          console.error('Matchmaking error:', err);
          setSuggestions([]);
        })
        .finally(() => setSuggestionsLoading(false));
    }, 600);

    return () => { if (matchDebounceRef.current) clearTimeout(matchDebounceRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.comp_id, hasCriteria, requiredSkills, requiredPreferences, selectedDate, startTime, endTime, resource]);

  // ── Select a suggested employee ────────────────────────────────
  const handleSuggestionSelect = (emp) => {
    const empOption = props.employees.find(e => e.id === emp.employee_id);
    if (!empOption) return;
    if (singleEmployeeOnly) {
      setEmployees([empOption]);
    } else {
      if (!employees.find(e => e.id === emp.employee_id)) {
        setEmployees([...employees, empOption]);
      }
    }
  };

  const [errors, setErrors] = useState({
    scheduleName: false,
    resource: false,
    employees: false,
    selectedDate: false,
    startTime: false,
    endTime: false,
    maxCapacity: false,
    taskType: false,
    customers: false,
  });

  const validate = () => {
    const newErrors = {
      scheduleName: !scheduleName,
      resource: !resource,
      employees: false, // optional for creation
      selectedDate: !selectedDate,
      startTime: !startTime || moment(startTime).isSameOrAfter(endTime),
      endTime: !endTime || moment(endTime).isSameOrBefore(startTime),
      maxCapacity: false,
      taskType: !taskType,
      customers: selectedCustomers?.length === 0 // mandatory field
    };
    setErrors(newErrors);
    setSubmitted(true);
    return Object.values(newErrors).every(error => !error);
  };

  const handleScheduleNameChange = (event) => {
    setScheduleName(event.target.value);
  };

  const handleResourceChange = (event, newValue) => {    
    if (newValue && newValue.resource_id === 'ADD_RESOURCE_OPTION') {      
      handleOpenAddResourceModal();
      setResource(''); // Ensure the "Add New Resource" option is not selected
      setMaxCapacity(''); // Clear the max capacity
      event.target.value = ''; // Clear the input value
    } else {
      setResource(newValue);
      setErrors({ ...errors, resource: false });
    }
  };

  const handleResourceInputChange = (event, newInputValue) => {
    console.log(newInputValue);
    
    if (newInputValue === '' || newInputValue === `+ Add New ${resourceLabelSingle}`) {
      setResourceName('');
    } else {
      setResourceName(newInputValue);
    }
  };

  const handleEmployeesChange = (event, newValue) => {
    if (props.user.permissions.ScheduleSessionForOtherEmployees) {
      setEmployees(newValue || []);
    } else {
      // Non-admins can only select themselves — keep first selection
      setEmployees(newValue?.length > 0 ? [newValue[newValue.length - 1]] : []);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    let date = moment(selectedDate);
    // Capacity check if customers chosen
    if (selectedCustomers.length > 0 && Number(maxCapacity) && selectedCustomers.length > Number(maxCapacity)) {
      props.onError(`The number of ${props.isDayCare ? 'Members' : 'Customers'} exceeds the maximum capacity`);
      return;
    }

    let resourceId = resource.resource_id;

    // If a customer address was selected, reuse an existing resource or auto-create one
    if (resource.isCustomerAddress) {
      // Check if a resource for this address was already created in a previous shift
      const existingResource = props.resources.find(r => r.resource_name === resource.resource_name);
      if (existingResource) {
        resourceId = existingResource.resource_id;
      } else {
      try {
        // Create a location for this address
        const locRes = await axios.post(APIURL + '/location', {
          address_line_1: resource.resource_name || '',
          address_line_2: '',
          city: '',
          state: '',
          zip: '',
          location_type: 'Customer Location',
          location_status: 'Personal',
          company_id: props.comp_id,
        });
        const newLocId = locRes.data?.id;
        if (!newLocId) {
          props.onError("Failed to create location for the selected address");
          return;
        }

        // Create a resource linked to the new location
        const resRes = await axios.post(APIURL + '/resources', {
          resource_name: resource.resource_name,
          capacity: maxCapacity || 1,
          location_id: newLocId,
          contact_id: null,
          notes: '',
        });
        resourceId = resRes.data;
        if (!resourceId) {
          props.onError("Failed to create resource for the selected address");
          return;
        }
        // Notify parent to add this new resource to the list (prevents re-creation on subsequent shifts)
        if (props.onAddResource) {
          props.onAddResource({
            resource_id: resourceId,
            resource_name: resource.resource_name,
            capacity: maxCapacity || 1,
            location_id: locRes.data?.id,
            contact_id: null,
            notes: '',
            resource_is_active: true,
          });
        }
      } catch (createErr) {
        console.error('Error auto-creating location/resource:', createErr);
        props.onError("Failed to set up location for the selected address");
        return;
      }
      }
    }

    let session = { 
      schedule_name: scheduleName,
      resource_id: resourceId,
      employee_ids: employees?.length > 0 ? employees.map(emp => emp.id) : [],
      date: date.format('YYYY-MM-DD'),
      start_time: moment(startTime).set({ year: date.year(), month: date.month(), date: date.date() }).utc().format('YYYY-MM-DDTHH:mm:00'),
      end_time: moment(endTime).set({ year: date.year(), month: date.month(), date: date.date() }).utc().format('YYYY-MM-DDTHH:mm:00'),
      max_capacity: maxCapacity,
      task_type: taskType,
      notes: notes ? notes : null,
      company_id: props.comp_id,
      created_by: props.user.emp_id,
      customers: selectedCustomers.map(c => c.cus_id),
      shift: singleEmployeeOnly,
    };
    
    try {
      let response = await axios.post(APIURL + '/master_schedule', session);
      const schedule_id = response.data.schedule_id;

      axios.post(APIURL + '/shift_action_log', {
        schedule_id,
        shift_emp_id: null,
        action: 'Shift Created',
        notes: null,
        action_by: props.user.emp_id,
        company_id: props.comp_id,
      }).catch(err => console.error('Error logging shift action:', err));

      if (employees.length > 0) {
        employees.forEach(emp => {
          axios.post(APIURL + '/shift_action_log', {
            schedule_id,
            shift_emp_id: emp.id,
            action: 'Employee Assigned',
            notes: null,
            action_by: props.user.emp_id,
            company_id: props.comp_id,
          }).catch(err => console.error('Error logging shift action:', err));
        });
      }

      // Notify available employees only if no employee is set (fire-and-forget)
      if (employees.length === 0) {
        axios.post(APIURL + '/sessions/notify-available', { schedule_id })
          .catch(notifyError => console.error('Error notifying available employees:', notifyError));
      }
      
      props.reloadScheduleData();
      props.onClose();
      props.onSuccess("Shift scheduled successfully");
    } catch (error) {
      if (error.response) {
        if (error.response.status === 400) {
          props.onError(error.response.data.message);
        } else {
          props.onError("An error occurred while scheduling shift");
        }
      } else {
        props.onError("An error occurred while scheduling shift");
      }
    }
  };

  useEffect(() => {
    if(!props.open) {
      setScheduleName('');
      setResource('');
      setEmployees([]);
      setSelectedDate(new Date());
      setStartTime(new Date());
      setEndTime(new Date());
      setMaxCapacity(1);
      setNotes('');
      setTaskType('');
      setSubmitted(false);
      setSelectedCustomers([]);
      setSingleEmployeeOnly(true);
      setRequiredSkills([]);
      setRequiredPreferences([]);
      setSuggestions([]);
      setSuggestionsLoading(false);
      setErrors({
        resource: false,
        employee: false,
        selectedDate: false,
        startTime: false,
        endTime: false,
        maxCapacity: false,
        taskType: false,
        customers: false,
      });
    }
  }, [props.open]);
  // PreAuth list for day care customer remaining units (task type dependent)
  useEffect(() => {
    if (props.isDayCare && taskType) {
      axios.get(APIURL + '/preAuth/taskType', { 
        params: { 
          companyID: props.comp_id, 
          taskType, 
          fromDate: moment(startTime).isValid() ? moment(startTime).subtract(2, 'months').format('MM/DD/YYYY') : null,
          toDate: moment(startTime).isValid() ? moment(startTime).endOf("day").format('MM/DD/YYYY') : null
        } 
      })
        .then(response => setPreAuthList(response.data.sort((a, b) => new Date(b.startDate) - new Date(a.startDate))))
        .catch(() => setPreAuthList([]));
    } else {
      setPreAuthList([]);
    }
  }, [taskType, props.isDayCare]);

  const customerLabelPlural = props.isDayCare ? 'Members' : 'Customers';

  const renderCustomerOption = (propsOption, option) => {
    const preAuth = preAuthList.find(item => item.cus_id === option.cus_id);
    const remainingUnits = preAuth ? Math.max(0, preAuth.remainingUnits) : null;
    const unitString = remainingUnits !== null ? `${remainingUnits}${remainingUnits === 1 ? ' Unit' : ' Units'}` : '';
    return (
      <li {...propsOption} key={option.cus_id}>
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {option.cus_id === 'assign_all' ? (
            <span style={{ color: '#3fa7d6', cursor: 'pointer' }}>Select All</span>
          ) : (
            <>
              <span>{option.cus_name}</span>
              {option.cus_status === 'Prospect' && (
                <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'gray' }}>Prospect</span>
              )}
              {unitString && <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'gray' }}>{unitString}</span>}
            </>
          )}
        </div>
      </li>
    );
  };

  useEffect(() => {
    if (props.slotScheduleInfo) {
      props.slotScheduleInfo.startTime ? setStartTime(props.slotScheduleInfo.startTime) : setStartTime(new Date());
      props.slotScheduleInfo.endTime ? setEndTime(props.slotScheduleInfo.endTime) : setEndTime(new Date());
      props.slotScheduleInfo.startTime ? setSelectedDate(props.slotScheduleInfo.startTime) : setSelectedDate(new Date());
      if (props.slotScheduleInfo.resource) {
        setResource(props.slotScheduleInfo.resource);
      } else {
        setResource('');
      }
    }
  }, [props.slotScheduleInfo]);

  const resourceLabelSingle = 'Location';
  const resourceLabelPlural = 'Locations';
  
  // Combine company resources with customer addresses for location dropdown
  const getLocationOptions = () => {
    const options = [...props.resources];
    const existingResourceNames = new Set(props.resources.map(r => r.resource_name));
    
    // Add addresses from selected customers (only if not already saved as a resource)
    if (selectedCustomers?.length > 0) {
      selectedCustomers.forEach(customer => {
        if (customer.cus_address) {
          const addresses = customer.cus_address.split(':').filter(addr => addr.trim());
          addresses.forEach((address, index) => {
            const trimmedAddress = address.trim();
            if (!existingResourceNames.has(trimmedAddress)) {
              options.push({
                resource_id: `customer_address_${customer.cus_id}_${index}`,
                resource_name: trimmedAddress,
                isCustomerAddress: true,
                customer_name: customer.cus_name
              });
            }
          });
        }
      });
    }
    
    return options;
  };

  const newResourceOption = {
    resource_id: 'ADD_RESOURCE_OPTION',
    resource_name: `+ Add New ${resourceLabelSingle}`,
  };

  const handleAddResource = (newResource) => {
    if (props.onAddResource) props.onAddResource(newResource);
    setResource(newResource);
    setMaxCapacity(newResource.capacity || '');
    setIsAddResourceModalOpen(false);
  };

  return (
    <Modal enforceFocus={false}
      open={props.open}
      onClose={() => {}}
      disableEscapeKeyDown
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={{ display: 'flex', justifyContent: 'center', overflowY: 'auto', p: 2 }}
    >
      <Box sx={{ width: 550, maxWidth: '100%', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2, height: 'fit-content' }}>
        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Schedule New Shift
          </Typography>
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{ p: 0.25 }}
            onClick={props.onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            label="Shift Name"
            fullWidth
            margin="normal"
            value={scheduleName}
            onChange={handleScheduleNameChange}
            error={errors.scheduleName && submitted}
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
          <FormControl fullWidth margin="normal">
            <Autocomplete
              multiple
              id="customer-select"
              options={props.customers || []}
              value={selectedCustomers}
              getOptionLabel={(option) => option.cus_name || ''}
              isOptionEqualToValue={(option, value) => option.cus_id === value.cus_id}
              onChange={(event, newValue) => {
                setSelectedCustomers(newValue || []);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={`Select ${customerLabelPlural}`}
                  placeholder={customerLabelPlural}
                  error={errors.customers && submitted}
                />
              )}
              renderOption={renderCustomerOption}
            />
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2} alignItems="center" sx={{ mt: 1, mb: 1 }}>
              <Grid size={4}>
                <DatePicker
                  label="Select Date"
                  value={moment(selectedDate).isValid() ? new Date(selectedDate) : null}
                  onChange={setSelectedDate}
                  slotProps={{ textField: { fullWidth: true, error: errors.selectedDate && submitted } }}
                />
              </Grid>
              <Grid size={4}>
                <TimePicker
                  label="Start Time"
                  value={startTime}
                  onChange={setStartTime}
                  slotProps={{ textField: { fullWidth: true, error: errors.startTime && submitted } }}
                />
              </Grid>
              <Grid size={4}>
                <TimePicker
                  label="End Time"
                  value={endTime}
                  onChange={setEndTime}
                  slotProps={{ textField: { fullWidth: true, error: errors.endTime && submitted } }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
          <FormControl fullWidth margin="normal">
            <InputLabel id="task-type-select-label">Task Type</InputLabel>
            <Select
              labelId="task-type-select-label"
              value={taskType}
              onChange={(e) => setTaskType(e.target.value)}
              error={errors.taskType && submitted}
            >
              {props.taskTypes.map((taskType, index) => (
                <MenuItem key={index} value={taskType}>
                  {taskType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <Autocomplete
              value={resource || null}
              inputValue={resourceName}
              options={[...getLocationOptions(), newResourceOption]}
              getOptionLabel={(option) => option.resource_name || ''}
              isOptionEqualToValue={(option, value) => option.resource_id === value.resource_id}
              renderOption={(props, option) => {
                if (option.resource_id === 'ADD_RESOURCE_OPTION') {
                  return (
                    <li {...props} style={{ color: '#3fa7d6' }}>
                      {option.resource_name}
                    </li>
                  );
                }
                return (
                  <li {...props}>
                    {option.resource_name}
                  </li>
                );
              }}
              onChange={handleResourceChange}
              onInputChange={handleResourceInputChange}
              renderInput={(params) => <TextField {...params} label={resourceLabelSingle} error={errors.resource && submitted} />}
            />
          </FormControl>

          {/* ── Employee Matchmaking Section ────────────────────────── */}
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            Find the right employee
          </Typography>
          <Grid container spacing={2}>
            <Grid size={6}>
              <Autocomplete
                multiple
                size="small"
                options={masterSkills}
                value={requiredSkills}
                onChange={(e, v) => setRequiredSkills(v)}
                renderInput={(params) => <TextField {...params} label="Skills needed" placeholder="e.g. CPR, Lifting" />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => <Chip {...getTagProps({ index })} label={option} size="small" key={option} />)
                }
              />
            </Grid>
            <Grid size={6}>
              <Autocomplete
                multiple
                size="small"
                options={masterPreferences}
                value={requiredPreferences}
                onChange={(e, v) => setRequiredPreferences(v)}
                renderInput={(params) => <TextField {...params} label="Other requirements" placeholder="e.g. Non-smoker" />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => <Chip {...getTagProps({ index })} label={option} size="small" key={option} />)
                }
              />
            </Grid>
          </Grid>
          <SuggestedEmployees
            suggestions={suggestions}
            loading={suggestionsLoading}
            onSelect={handleSuggestionSelect}
            selectedIds={employees.map(e => e.id)}
            visible={hasCriteria}
          />
          <Divider sx={{ my: 1.5 }} />

          <FormControl fullWidth margin="normal">
            <Autocomplete
              multiple
              id="employee-select"
              options={props.employees}
              getOptionLabel={(option) => option.employee_name}
              renderInput={(params) => <TextField {...params} label="Employees" error={errors.employees && submitted} />}
              onChange={handleEmployeesChange}
              value={employees}
              disabled={!props.user.permissions.ScheduleSessionForOtherEmployees && employees.length >= 1}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    <ListItemText primary={option.employee_name} />
                  </li>
                )
              }}
            />
          </FormControl>
          <TextField
            margin="normal"
            fullWidth
            label="Notes"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
          <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
            Schedule Shift
          </Button>
        </Box>
          {isAddResourceModalOpen && (
            <AddResourceModal
              open={isAddResourceModalOpen}
              onClose={handleCloseAddResourceModal}
              locations={props.locations} // Pass locations prop
              contacts={props.contacts} // Pass contacts prop
              isDayCare={props.isDayCare} // Pass isDayCare prop
              onAddResource={handleAddResource} // Pass handler for adding resource
            />
          )}
      </Box>
    </Modal>
  );
}

export default ScheduleShiftModal;
